import { ChevronDownIcon, XIcon } from "lucide-react";
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

import { useHeaderContext } from "../headers/HeaderContext";
import { CustomToolTip } from "../ui/CustomToolTip";

import {
  applyClearedFortnightFilterParams,
  areAllValuesNull,
  formatFortnightQueryDate,
  getSliceCount,
  shouldShowClearFilter,
} from "./filter.utils";
import FilterEntry from "./FilterEntry";
import type { FilterType } from "./filters.types";
import FortnightPicker, { isCurrentFortnight } from "./FortnightPicker";
import { useFilterMeasurements } from "./useFilterMeasurements";

import { useDataTableSlotContext } from "@/components/dataTable/DataTableSlotContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import type { useQueryFilterType } from "@/hooks/query/useQueryParams";
import type { Any } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

interface FiltersProps<T, P> {
  filters: FilterType<T, P>;
  customFilter?: ReactNode;
  showFilters: boolean;
  filterOperation: useQueryFilterType;
  hide?: boolean;
  hideClearButton?: boolean;
  clearButtonSize?: "sm" | "lg";
}

/**
 * Renders the set of active filters for a data table or list view.
 *
 * @param {FiltersProps<T, P>} props - Filter configuration and operations.
 * @returns {JSX.Element | null} Filter bar with responsive collapsing.
 */
const Filters = <T, P>(props: FiltersProps<T, P>): JSX.Element | null => {
  const {
    filters,
    customFilter,
    showFilters = true,
    filterOperation,
    hide,
    hideClearButton,
    clearButtonSize = "lg",
  } = props;
  const { setFilters, resetSorting } = useDataTableSlotContext<T, P>();
  const fortnightRef = useRef<{ reset: () => void } | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { keys, clearParams, getAllValue, mutateParams } = filterOperation;
  const { hasTabs } = useHeaderContext();

  const { containerRef, width, measuredWidths } =
    useFilterMeasurements(filters);

  const { filterEntries, hasFortnightFilter, filtersWithoutPagination } =
    useMemo(() => {
      const filterKeys = Object.keys(filters);
      const hasFortnightFilter = filterKeys.includes("fortnight");
      const filtersWithoutPagination = filterKeys.filter(
        (key) => !["limit", "offset", "currentPage", "fortnight"].includes(key),
      );

      return {
        hasFortnightFilter,
        filtersWithoutPagination,
        filterEntries: Object.entries(filters),
      };
    }, [filters]);

  const sliceCount = useMemo(
    () =>
      getSliceCount({
        width,
        filters,
        measuredWidths,
      }),
    [width, filters, measuredWidths],
  );

  const handleClearFilterForFortnight = useCallback((): void => {
    resetSorting?.();
    mutateParams(applyClearedFortnightFilterParams, { replace: true });
  }, [mutateParams, resetSorting]);

  const handleClearFilter = useCallback((): void => {
    if (Object.keys(filters).includes("fortnight")) {
      handleClearFilterForFortnight();
    } else {
      resetSorting?.();
      clearParams();
    }
  }, [clearParams, filters, handleClearFilterForFortnight, resetSorting]);

  // Separate handler for tab-switch-triggered filter reset that does NOT
  // call resetSorting — that would overwrite the tab's persisted sort.
  const resetFiltersForTabChange = useCallback((): void => {
    clearParams();
  }, [clearParams]);

  useEffect(() => {
    if (setFilters) {
      setFilters({
        customFilter,
        filters: filters as FilterType<T, P>,
        showFilters,
        filterOperation,
        handleClearFilter,
        resetFiltersForTabChange,
        clearButtonSize,
      });
    }

    return (): void => {
      setFilters?.(null);
    };
  }, [
    customFilter,
    filterOperation,
    filters,
    handleClearFilter,
    setFilters,
    showFilters,
    clearButtonSize,
  ]);

  if (hide) {
    return null;
  }

  const handleFortnightDateRangeChange = (selectedDates: Date[]): void => {
    const currentFortnightValues = getAllValue("fortnight");
    const hasExistingFortnight =
      currentFortnightValues && currentFortnightValues.length === 2;
    const nextFortnightValues = [
      formatFortnightQueryDate(selectedDates?.at(0)),
      formatFortnightQueryDate(selectedDates?.at(-1)),
    ];

    if (isInitialLoad && hasExistingFortnight) {
      setIsInitialLoad(false);

      return;
    }

    if (
      currentFortnightValues?.[0] === nextFortnightValues[0] &&
      currentFortnightValues?.[1] === nextFortnightValues[1]
    ) {
      setIsInitialLoad(false);

      return;
    }

    mutateParams((params) => {
      params.delete("fortnight");
      nextFortnightValues.forEach((fortnightValue) => {
        if (fortnightValue) {
          params.append("fortnight", fortnightValue);
        }
      });
      params.delete("date");
    });
    setIsInitialLoad(false);
  };

  const renderFortnightPicker = (): JSX.Element | null =>
    hasFortnightFilter ? (
      <div data-filter-key='fortnight'>
        <FortnightPicker
          key='fortnight'
          ref={fortnightRef}
          setDateRange={handleFortnightDateRangeChange}
        />
      </div>
    ) : null;

  const renderFilterEntries = (
    entries: [string, Any][],
    side?: "top" | "right" | "bottom" | "left",
  ): (JSX.Element | null)[] =>
    entries.map(([key, value]) => (
      <div key={key} data-filter-key={key}>
        <FilterEntry
          filterKey={key}
          value={value}
          side={side}
          filters={filters}
          filterOperation={filterOperation}
        />
      </div>
    ));

  const renderDropdownMenu = (
    entries: typeof filterEntries,
    sliceStart: number = 0,
    sliceEnd?: number,
  ): ReactNode => {
    const slicedFilters = sliceEnd
      ? entries?.slice(sliceStart, sliceEnd)
      : entries?.slice(sliceStart);

    if (!slicedFilters?.length) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild className='h-10'>
          <Button
            variant='outline'
            className='px-2 font-normal text-gray-400 dashed-border'
          >
            More <ChevronDownIcon className='w-4 h-4 ml-2' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='space-y-2'>
          {slicedFilters.map(([key, value]) => (
            <FilterEntry
              key={key}
              filterKey={key}
              value={value}
              side='right'
              filters={filters}
              filterOperation={filterOperation}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderFiltersByScreenSize = (): ReactNode => {
    const filterCount = filtersWithoutPagination.length;
    if (width > 0 && width < 450) {
      if (hasFortnightFilter) {
        return (
          <>
            {renderFortnightPicker()}
            {renderDropdownMenu(filterEntries)}
          </>
        );
      }

      if (filterCount > 0) {
        return renderDropdownMenu(filterEntries);
      }

      return null;
    }

    if (width > 0 && sliceCount < filterCount) {
      return (
        <div className='flex gap-2 '>
          {hasFortnightFilter && renderFortnightPicker()}
          {renderFilterEntries(filterEntries.slice(0, sliceCount))}
          {filterCount > 0 && renderDropdownMenu(filterEntries, sliceCount)}
        </div>
      );
    }

    return (
      <div className='flex gap-2 '>
        {hasFortnightFilter && renderFortnightPicker()}
        {renderFilterEntries(filterEntries)}
      </div>
    );
  };

  const showClearFortnight = (): boolean => {
    const nonFortnightKeys = Object.keys(keys).filter(
      (key) =>
        key !== "fortnight" &&
        key !== "search" &&
        !key.includes("_search") &&
        // `ig_` stands for ignore key; UI-state mirrors like ig_activeTab
        // are not real filters.
        !key.startsWith("ig_"),
    );
    const hasOtherFilterValues = nonFortnightKeys.some(
      (key) =>
        keys[key] !== null && keys[key] !== undefined && keys[key] !== "",
    );

    if (hasOtherFilterValues) {
      return true;
    }

    const fortnightValue = getAllValue("fortnight");

    if (!fortnightValue) {
      return false;
    }

    const [startDate, endDate] = fortnightValue;

    if (!startDate || !endDate) {
      return false;
    }

    return !isCurrentFortnight({
      start: new Date(startDate),
      end: new Date(endDate),
    });
  };

  const clearFilterIgnoreKeys = [
    "id",
    "search",
    "orderBy",
    "sortBy",
    "status",
    "matcherStartDate",
    "matcherEndDate",
    "releaseCustomerIds",
    "releaseSearch",
    "releaseToDate",
    ...Object.keys(keys).filter((key) => key.includes("_search")),
    // `ig_` stands for "ignore key": any URL param prefixed with `ig_`
    // (e.g. ig_activeTab) is a UI-state mirror, not a user-applied filter,
    // so it should never make the Clear Filter button appear.
    ...Object.keys(keys).filter((key) => key.startsWith("ig_")),
  ];

  return (
    <div
      ref={containerRef}
      className={cn("flex-1 min-w-0 flex items-center gap-3", {
        "justify-end": hasTabs,
        "justify-start": !hasTabs,
      })}
    >
      {customFilter && customFilter}
      {showFilters && filterEntries.length > 0 && (
        <div className='flex items-center gap-2 my-2'>
          {renderFiltersByScreenSize()}
          {!areAllValuesNull(keys) &&
            shouldShowClearFilter(keys, clearFilterIgnoreKeys) &&
            !hideClearButton &&
            showClearFortnight() && (
              <CustomToolTip
                toolTipContent={
                  clearButtonSize === "sm" ? "Clear Filter" : undefined
                }
                toolTipTrigger={
                  <Button
                    variant='ghost'
                    className={cn("px-2 h-10", {
                      "px-3": clearButtonSize === "sm",
                    })}
                    onClick={() => handleClearFilter()}
                  >
                    {clearButtonSize === "sm" ? (
                      <XIcon size={18} className='text-red-500' />
                    ) : (
                      "Clear Filter"
                    )}
                  </Button>
                }
              />
            )}
        </div>
      )}
    </div>
  );
};

export default Filters;
